import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonAvailableType } from '../../enums/mat-button-types';
import { MediaViewerContentType } from '../../enums/media-viewer-content-types';
import { MediaViewerDialogModel } from '../../models/media-viewer-dialog-model';
import { MediaViewerDialogComponent } from '../../components/media-viewer-dialog/media-viewer-dialog.component';

@Component({
    selector: 'apcam-media-viewer',
    templateUrl: './media-viewer.component.html',
    styleUrls: ['./media-viewer.component.scss'],
    imports: [MatButtonModule, MatIconModule],
})
export class MediaViewerComponent {
    // #region Component I/O
    /**
     * Angular material color of the main button, which opens the confirmation
     *
     * @default 'primary'
     * */
    @Input()
    AMColor: 'primary' | 'accent' | 'warn' = 'primary';

    /**
     * Aria-label property for the main button, which opens the confirmation. This property must accept
     * a value which utilizes the multilinguality (e.g. ngx-translate).
     *
     * @required
     */
    @Input()
    ariaLabelButton: string = '';

    /**
     * CSS class for the main button, which opens the confirmation.
     */
    @Input()
    buttonClass: string = '';

    /**
     * Text inside the main button, which opens the confirmation. Does not work when the button type of ICON,
     * FAB (without 'fabExtended' property) or MINI_FAB is selected.
     */
    @Input()
    buttonText: string = '';

    /**
     * Type of the button. You can choose any button type as defined by Angular material.
     * See the 'MatButtonAvailableTypes' enum.
     *
     * @default MatButtonAvailableType.FLAT
     */
    @Input()
    buttonType: MatButtonAvailableType = MatButtonAvailableType.FLAT;

    /**
     *
     * Content type of the file to display. Must be one of the 'MediaViewerContentType' enum values.
     *
     * @required
     * @default application/pdf
     */
    @Input({ required: true })
    contentType: MediaViewerContentType =
        MediaViewerContentType.APPLICATION_PDF;

    /**
     * Whether the main button is disabled
     */
    @Input()
    disabled: boolean = false;

    /**
     * Whether the main button is extended. Has effect only if the 'buttonType' property is set to "FAB".
     */
    @Input()
    fabExtended: boolean = false;

    /**
     * Name of the file.
     */
    @Input()
    fileName: string = '';

    /**
     * A flag signalizing whether the print button shall be hidden for the viewer or not.
     */
    @Input()
    hidePrint: boolean = false;

    /**
     * Name of the icon to be displayed within the main button. To display an icon, you need to set
     * the 'withIcon' property as 'true'.
     */
    @Input()
    iconName: string = '';

    /**
     * Url of the file which is being rendered.
     *
     * @required
     */
    @Input({ required: true })
    url: string = '';

    /**
     * Whether display an icon inside the main button.
     */
    @Input()
    withIcon: boolean = false;

    @Input()
    dialogWidth: string = '';

    // Outputs //

    /**
     * Emitter of signal to launch the download operation.
     */
    @Output()
    download: EventEmitter<void> = new EventEmitter();

    /**
     * Emitter of signal to launch the print operation.
     */
    @Output()
    print: EventEmitter<void> = new EventEmitter();
    //#endregion

    MatButtonAvailableType = MatButtonAvailableType;

    constructor(private readonly dialog: MatDialog) {}

    showDialog() {
        const dialogData: MediaViewerDialogModel = {
            contentType: this.contentType,
            fileName: this.fileName,
            url: this.url,
            hidePrint: this.hidePrint,
        };
        this.dialog.open(MediaViewerDialogComponent, {
            // Here, we specify the confirmation window to span across the whole viewport height and half of the screen width on larger screens
            height: '100vh',
            maxWidth: '',
            width: this.dialogWidth,
            position: { right: '0px' },
            data: dialogData,
            panelClass: [
                'w-full!',
                'max-w-full!',
                'md:max-w-[80vw]!',
                'lg:max-w-[60vw]!',
                'xl:max-w-[50vw]!',
            ],
        });
    }
}
